<template>
  <div
    class="BlockRepeater"
    :class="{ 'inline': inline, 'disabled': disabled, 'no-items': !itemArray?.length }"
  >
    <div
      v-if="!isLimited"
      class="BlockRepeater__header"
    >
      <button
        v-if="!viewer && !disabled"
        class="BlockRepeater__add"
        :disabled="isLimited"
        @click="addItem"
      >
        <i class="icon fa-fw fal fa-circle-plus" />
        <span v-if="label">{{ label[$i18n.locale] || label }}</span>
      </button>
      <h3
        v-if="title"
        class="mt-3"
      >
        {{ setTitle }}
      </h3>
    </div>
    <small
      v-if="note"
      class="form-text mb-2"
      style="color: #0c7fa8;"
    >
      <i class="fal fa-info-circle mr-1" />{{ note }}
    </small>
    <div class="BlockRepeater__body">
      <RepeaterItem
        v-for="id in itemArray"
        :id="id"
        :key="id"
        :inline="inline"
        :border="border"
        :divider="divider"
        :viewer="viewer"
        :compact="compact"
        :disabled="disabled || (formProps && itemArray.length === 1)"
        :permanent="isPermanent({ id })"
        :deleted="isDeleted({ id })"
        @delete="removeItem(id)"
      >
        <slot :id="id" />
      </RepeaterItem>
    </div>
    <b-alert
      v-if="expectedCount && itemArray.length !== expectedCount"
      :show="true"
      variant="danger"
      class="mt-2"
    >
      {{ $t('Process.repeaterCountMismatch', { actual: itemArray.length, expected: expectedCount }) }}
    </b-alert>
  </div>
</template>

<script>
import RepeaterItem from './BlockRepeaterItem.vue'
export default {
  name: 'BlockRepeater',
  components: {
    RepeaterItem,
  },
  props: {
    title: {
      type: [String, Object],
      default: '',
    },
    label: {
      type: [String, Object],
      default: '',
    },
    path: {
      type: String,
      default: '',
    },
    values: {
      type: Object,
      default: function () {
        return {}
      },
    },
    /**
     * Compact, inline visualisation
     */
    inline: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    border: {
      type: Boolean,
      default: false,
    },
    divider: {
      type: Boolean,
      default: false,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    permanent: {
      type: [Boolean, Function],
      default: false,
    },
    deleted: {
      type: Function,
      default: () => false,
    },
    minItems: {
      type: Number,
      default: 0,
    },
    limit: {
      type: Number,
      default: 0,
    },
    /**
     * Viewer (no UI)
     */
    viewer: {
      type: Boolean,
      default: false,
    },
    reverseOrder: {
      type: Boolean,
      default: false
    },
    formProps: {
      type: Object,
      default: null
    },
    note: {
      type: String,
      default: ''
    },
    expectedCount: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      currentId: 0,
      itemArray: [],
    }
  },
  computed: {
    setTitle () {
      return this.title?.[this.$i18n.locale] || this.title
    },
    isLimited() {
      return !!this.limit && this.itemArray.length >= this.limit
    },
  },
  watch: {
    values: {
      handler() {
        // Reload the list based on the input data from the API whenever this input data changes
        this.setAvailableBlocks()
      },
      deep: true,
    },
  },
  created() {
    // Load available data from the API
    this.setAvailableBlocks()
  },
  methods: {
    setAvailableBlocks() {
      // Move to the specified path in the nested values object
      let segments = this.path.split('.')
      let values = this.values
      segments.forEach(segment => {
        values = values[segment] || {}
      })

      // Merge saved items with any locally-added (unsaved) items so that unrelated value changes (e.g. file uploads) don't wipe the user's in-progress additions.
      const savedKeys = Object.keys(values).map(key => parseInt(key)).filter(Number.isFinite)
      const merged = Array.from(new Set([...savedKeys, ...this.itemArray]))
        .filter(Number.isFinite)
        .sort((a, b) => a - b)

      if (this.reverseOrder) {
        merged.reverse()
      }

      this.itemArray = merged
      this.currentId = merged.length ? Math.max(...merged) + 1 : 0

      this.$nextTick(() => {
        // Pre-render items based on "minItems" property //
        if (this.minItems && this.minItems > this.itemArray.length) {
          const min = this.minItems - this.itemArray.length;

          [...Array(min)].forEach(() => this.addItem());
        }
      })
    },
    isPermanent({ id }) {
      if (this.permanent === false || this.permanent === null) return false
      if (this.permanent === true) return true

      return this.permanent({ index: id })
    },
    isDeleted({ id }) {
      return this.deleted({ index: id })
    },
    addItem(e) {
      e?.preventDefault()
      this.$emit('changed', { index: this.currentId, items: this.itemArray, path: this.path })

      const method = this.reverseOrder ? 'unshift': 'push'
      this.itemArray[method](this.currentId)
      this.currentId++

    },
    removeItem(id) {
      this.itemArray = this.itemArray.filter(item => item !== id)
      this.$emit('removed', { index: id })
    },
  },
}
</script>

<style lang="scss">
.BlockRepeater {
  display: block;
  width: 100%;

  &__header {
    display: flex;
    flex-direction: reverse;
    // justify-content: space-between;
    align-items: center;
    margin-bottom: $padding;
  }

  &__body {
    display: flex;
    flex-direction: column-reverse;
  }

  &__add {
    // width: 100%;
    background-color: transparent;
    // place-items: center;
    // grid-template-columns: 60px 1fr;
    border: 1px dashed $gray-500;
    border-radius: 3px;
    padding: $padding-sm;
    font-size: 1.5em;
    color: $primary-darkblue;
    margin-right: $padding-sm;

    &:hover {
      background-color: $gray-200;
    }

    span {
      margin-left: $padding-sm
    }
  }

  &.box {
    border: 1px solid #adb5bd;
    border-radius: 3px;
    padding: 12.5px;
    &.no-items {
      border: none;
      padding: 0;
    }
  }

  &.inline {
    .BlockRepeater {
      &__header {
        margin-bottom: 0;
      }

      &__add {
        font-size: 1em;
        border: none;

        &:hover {
          color: $primary-darkblue;
          text-decoration: underline;
          background-color: transparent;
        }
      }
    }
    &.asset-create {
      .BlockRepeater {
        &__add {
          font-size: 1.3em;
          border: none;
          order: 1;
          margin-left: 1em;
          display: flex;
          align-items: center;
          margin-top: 0.3em;

          &:hover {
            text-decoration: none;
          }
        }
        &__body {
            .form-group:first-child {
              display: flex;
              align-items: flex-start;
              margin-top: 5px;
            }
          }


      }
      .BlockRepeaterItem {
        &.inline {
         align-items: flex-start;
        }
        &__body {
          flex: 0 1 70%;
        }
      }
    }
    &.parkinglot-repeater {
        position: relative;
        top: -1.4em;
        width: calc(100% - 3rem);
        margin-left: 3rem;
      }
  }

  &.disabled {
    .BlockRepeater {
      &__add {
        color: $gray-500 !important;
        cursor: not-allowed;
      }
    }
  }
  .invalid-feedback {
    position: absolute;
  }
}
</style>
